import {
    Component,
    Input,
    OnChanges,
    SimpleChanges,
    ChangeDetectionStrategy
} from "@angular/core";
import { LocationStrategy, PathLocationStrategy } from "@angular/common";
// import { area, line } from "d3-shape";
// https://github.com/d3/d3/blob/5f0bea71c6f0c1928fbb0394def13641b38a3e4b/CHANGES.md
import * as d3 from "d3";

import { id } from "../utils/id";
import { sortLinear, sortByTime, sortByDomain } from "../utils/sort";

@Component({
    // tslint:disable-next-line:component-selector
    selector: "g[ng-charts-line-series]",
    template: `
    <svg:g>
      <defs>
        <svg:g ng-charts-svg-linear-gradient ng-if="hasGradient"
          [color]="colors.getColor(data.name)"
          orientation="vertical"
          [name]="gradientId"
          [stops]="gradientStops"
        />
      </defs>
      <svg:g ng-charts-area
        class="line-highlight"
        [data]="data"
        [path]="areaPath"
        [fill]="hasGradient ? gradientUrl : colors.getColor(data.name)"
        [opacity]="0.25"
        [startOpacity]="0"
        [gradient]="true"
        [stops]="areaGradientStops"
        [class.active]="isActive(data)"
        [class.inactive]="isInactive(data)"
      />
      <svg:g ng-charts-line
        class="line-series"
        [data]="data"
        [path]="path"
        [stroke]="hasGradient ? gradientUrl : colors.getColor(data.name)"
        [class.active]="isActive(data)"
        [class.inactive]="isInactive(data)"
      />
     <svg:g ng-charts-area
        class="line-series-range"
        [data]="data"
        [path]="outerPath"
        [fill]="hasGradient ? gradientUrl : colors.getColor(data.name)"
        [class.active]="isActive(data)"
        [class.inactive]="isInactive(data)"
        [opacity]="rangeFillOpacity"
      />
    </svg:g>
  `,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LineSeriesComponent implements OnChanges {

    @Input() data;
    @Input() xScale;
    @Input() yScale;
    @Input() colors;
    @Input() scaleType;
    @Input() curve: any;
    @Input() activeEntries: any[];
    @Input() rangeFillOpacity: number;

    path: string;
    outerPath: string;
    areaPath: string;
    gradientId: string;
    gradientUrl: string;
    hasGradient: boolean;
    gradientStops: any[];
    areaGradientStops: any[];

    constructor(private location: LocationStrategy) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.update();
    }

    update(): void {
        this.updateGradients();

        const line = this.getLineGenerator();
        const area = this.getAreaGenerator();
        const range = this.getRangeGenerator();

        const data = this.sortData(this.data.series);

        this.path = line(data) || "";
        this.outerPath = range(data) || "";
        this.areaPath = area(data) || "";
    }

    getLineGenerator(): any {
        // https://github.com/d3/d3/blob/5f0bea71c6f0c1928fbb0394def13641b38a3e4b/CHANGES.md
        // return line<any>()
        return d3.svg.line<any>()
            .x(d => {
                const label = d.name;
                let value;
                if (this.scaleType === "time") {
                    value = this.xScale(label);
                } else if (this.scaleType === "linear") {
                    value = this.xScale(Number(label));
                } else {
                    value = this.xScale(label);
                }
                return value;
            })
            .y(d => this.yScale(d.value))
            .interpolate(this.curve);
            // .curve(this.curve);
    }

    getRangeGenerator(): any {
        // https://github.com/d3/d3/blob/5f0bea71c6f0c1928fbb0394def13641b38a3e4b/CHANGES.md
        // https://github.com/d3/d3-3.x-api-reference/blob/master/API-Reference.md
        // return area<any>()
        return d3.svg.area<any>()
            .x(d => {
                const label = d.name;
                let value;
                if (this.scaleType === "time") {
                    value = this.xScale(label);
                } else if (this.scaleType === "linear") {
                    value = this.xScale(Number(label));
                } else {
                    value = this.xScale(label);
                }
                return value;
            })
            .y0(d => this.yScale(d.min ? d.min : d.value))
            .y1(d => this.yScale(d.max ? d.max : d.value))
            .interpolate(this.curve);
            // .curve(this.curve);
    }

    getAreaGenerator(): any {
        const xProperty = (d) => {
            const label = d.name;
            return this.xScale(label);
        };
        // https://github.com/d3/d3/blob/5f0bea71c6f0c1928fbb0394def13641b38a3e4b/CHANGES.md
        // https://github.com/d3/d3-3.x-api-reference/blob/master/API-Reference.md
        // return area<any>()
        return d3.svg.area<any>()
            .x(xProperty)
            .y0(() => this.yScale.range()[0])
            .y1(d => this.yScale(d.value))
            .interpolate("linear");
            // .curve(this.curve);
    }

    sortData(data) {
        if (this.scaleType === "linear") {
            data = sortLinear(data, "name");
        } else if (this.scaleType === "time") {
            data = sortByTime(data, "name");
        } else {
            data = sortByDomain(data, "name", "asc", this.xScale.domain());
        }

        return data;
    }

    updateGradients() {
        if (this.colors.scaleType === "linear") {
            this.hasGradient = true;

            const pageUrl = this.location instanceof PathLocationStrategy
                ? this.location.path()
                : "";

            this.gradientId = "grad" + id().toString();
            this.gradientUrl = `url(${pageUrl}#${this.gradientId})`;
            const values = this.data.series.map(d => d.value);
            const max = Math.max(...values);
            const min = Math.min(...values);
            this.gradientStops = this.colors.getLinearGradientStops(max, min);
            this.areaGradientStops = this.colors.getLinearGradientStops(max);
        } else {
            this.hasGradient = false;
            this.gradientStops = undefined;
            this.areaGradientStops = undefined;
        }
    }

    isActive(entry): boolean {
        if (!this.activeEntries) { return false; }
        const item = this.activeEntries.find(d => {
            return entry.name === d.name;
        });
        return item !== undefined;
    }

    isInactive(entry): boolean {
        if (!this.activeEntries || this.activeEntries.length === 0) { return false; }
        const item = this.activeEntries.find(d => {
            return entry.name === d.name;
        });
        return item === undefined;
    }

}
